import { Component, OnInit, OnDestroy } from '@angular/core';
import { first } from 'rxjs/operators';
import { Subscription, interval } from 'rxjs';
import { AuthenticationService, AlertService } from '@app/_services';
import { Router } from '@angular/router';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { ConstantService } from '@app/_services/constant.service';
import { GuestArrivalService } from '../../guest-arrival.service';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SmsComponent } from '../sms/sms.component';
import { AiDialogService } from '@app/_components/ai/ai-dialog/ai-dialog.service';
import { DialogService } from 'primeng/dynamicdialog';

export type ArrivalRowStatus = 'GREEN' | 'AMBER' | 'RED' | 'GREY';

export interface ArrivalRow {
	reservationId: number;
	orderId: number;
	locationId: number;
	propertyCode: string;
	shortTermRentalId: number;
	guestFirstName: string;
	guestLastName: string;
	checkInDate: string;
	checkOutDate: string;
	mobilePhone: string;
	accessCodeFrontDoor: string;
	accessCodeHoaGate: string;
	// agent-board fields
	boardStatus: ArrivalRowStatus;
	messageSentAt: string | null;
	breezewaySummary: string;
	breezewayPct: number | null;
	manualHold: boolean;
}

@Component({
	selector: 'app-guest-arrival-list',
	templateUrl: './guest-arrival-list.component.html',
	styleUrls: ['./guest-arrival-list.component.css'],
	providers: [{ provide: MAT_DIALOG_DATA, useValue: {} }, MessageService, DialogService, AiDialogService],
	standalone: false
})
export class GuestArrivalListComponent implements OnInit, OnDestroy {

	datasource: any[];
	reservation: ArrivalRow;
	reservations: ArrivalRow[] = [];
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	locations: any[];

	// board controls
	autoMode = false;
	lastRefreshed: Date | null = null;

	// send-log keyed by reservationId to avoid duplicates across refreshes
	private sendLog: Map<number, string> = new Map();

	// interval subscriptions
	private boardRefreshSub?: Subscription;
	private autoModeSub?: Subscription;

	// pending Breezeway fetch queue (staggered to avoid flooding)
	private bwFetchQueue: number[] = [];
	private bwFetchTimer: any;

	constructor(
		public authenticationService: AuthenticationService,
		public reservationService: GuestArrivalService,
		public alertService: AlertService,
		public constantService: ConstantService,
		private dialog: MatDialog,
		private messageService: MessageService,
		public router: Router,
		private aiDialogService: AiDialogService
	) {
		this.locations = this.constantService.locations;
	}

	ngOnInit() {
		this.startBoardRefresh();
	}

	ngOnDestroy() {
		this.boardRefreshSub?.unsubscribe();
		this.autoModeSub?.unsubscribe();
		if (this.bwFetchTimer) { clearTimeout(this.bwFetchTimer); }
	}

	// ─── Auto-refresh every 60 seconds ───────────────────────────────────────

	private startBoardRefresh() {
		this.boardRefreshSub = interval(60000).subscribe(() => {
			if (this.lazyLoadEvent) {
				this.loadLazy(this.lazyLoadEvent);
			}
		});
	}

	// ─── Auto-mode: poll every 5 minutes and auto-send GREEN rows ────────────

	toggleAutoMode() {
		this.autoMode = !this.autoMode;
		if (this.autoMode) {
			this.autoModeSub = interval(300000).subscribe(() => this.autoSendGreen());
		} else {
			this.autoModeSub?.unsubscribe();
			this.autoModeSub = undefined;
		}
	}

	private autoSendGreen() {
		const greenRows = this.reservations.filter(r => r.boardStatus === 'GREEN' && !r.messageSentAt);
		for (const row of greenRows) {
			this.sendArrivalSMS(row, true);
		}
	}

	// ─── Lazy load (existing pattern, augmented) ─────────────────────────────

	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			this.lazyLoadEvent = event;

			if (Object.keys(event.filters).length > 0) {
				if (!event.multiSortMeta) { event.multiSortMeta = []; }
				event.multiSortMeta.push({ field: 'checkInDateTime', order: -1 });
			} else {
				if (!event.multiSortMeta) { event.multiSortMeta = []; }
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			const pageNumber = Math.round(event.first / event.rows);
			this.reservationService.findByAgentListFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(
					res => {
						this.datasource = res.data.reservations;
						this.recordTotal = res.page.recordTotal;
						const rows: ArrivalRow[] = (res.data.reservations || []).map((r: any) => this.toArrivalRow(r));
						this.reservations = rows;
						this.lastRefreshed = new Date();
						this.scheduleBwFetch(rows);
					},
					error => {
						this.alertService.error(error);
						this.loading = false;
					},
					() => { this.loading = false; }
				);

			if (this.datasource) {
				// keep slice for display consistency while new data loads
			}
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}

	// ─── Row mapping ──────────────────────────────────────────────────────────

	private toArrivalRow(r: any): ArrivalRow {
		const sentAt = this.sendLog.get(r.reservationId) || null;
		return {
			reservationId: r.reservationId,
			orderId: r.orderId,
			locationId: r.locationId,
			propertyCode: r.propertyCode,
			shortTermRentalId: r.shortTermRentalId,
			guestFirstName: r.guestFirstName,
			guestLastName: r.guestLastName,
			checkInDate: r.checkInDate,
			checkOutDate: r.checkOutDate,
			mobilePhone: r.mobilePhone,
			accessCodeFrontDoor: r.accessCodeFrontDoor,
			accessCodeHoaGate: r.accessCodeHoaGate,
			boardStatus: this.computeStatus(r, sentAt),
			messageSentAt: sentAt,
			breezewaySummary: 'Loading...',
			breezewayPct: null,
			manualHold: false
		};
	}

	private computeStatus(r: any, sentAt: string | null): ArrivalRowStatus {
		if (sentAt) { return 'GREY'; }
		if (!r.accessCodeFrontDoor) { return 'RED'; }
		// Will be updated after Breezeway fetch; default to AMBER until confirmed
		return 'AMBER';
	}

	// ─── Breezeway task fetch (staggered, 800ms apart) ────────────────────────

	private scheduleBwFetch(rows: ArrivalRow[]) {
		const ids = rows
			.filter(r => r.shortTermRentalId)
			.map(r => r.shortTermRentalId);
		// deduplicate
		this.bwFetchQueue = [...new Set(ids)];
		this.processBwQueue();
	}

	private processBwQueue() {
		if (this.bwFetchTimer) { clearTimeout(this.bwFetchTimer); }
		if (!this.bwFetchQueue.length) { return; }
		const id = this.bwFetchQueue.shift();
		this.fetchBwStatus(id);
		this.bwFetchTimer = setTimeout(() => this.processBwQueue(), 800);
	}

	private fetchBwStatus(shortTermRentalId: number) {
		this.reservationService.getPropertyTasks(shortTermRentalId).pipe(first()).subscribe(
			res => {
				const tasks: any[] = (res?.data?.properties) || [];
				const total = tasks.length;
				const complete = tasks.filter(t => (t.status || '').toLowerCase() === 'approved').length;
				const pct = total > 0 ? Math.round((complete / total) * 100) : null;
				const summary = total > 0 ? `${complete}/${total} tasks` : 'No tasks';
				// apply to matching rows
				this.reservations
					.filter(r => r.shortTermRentalId === shortTermRentalId)
					.forEach(r => {
						r.breezewayPct = pct;
						r.breezewaySummary = summary;
						// Re-evaluate status now that we have Breezeway data
						if (!r.messageSentAt && !r.manualHold) {
							if (!r.accessCodeFrontDoor) {
								r.boardStatus = 'RED';
							} else if (pct === 100 || (complete === total && total > 0)) {
								r.boardStatus = 'GREEN';
							} else {
								r.boardStatus = 'AMBER';
							}
						}
					});
			},
			() => {
				// Breezeway unavailable — keep AMBER for rows with door code
				this.reservations
					.filter(r => r.shortTermRentalId === shortTermRentalId)
					.forEach(r => { r.breezewaySummary = 'Unavailable'; });
			}
		);
	}

	// ─── Row actions ──────────────────────────────────────────────────────────

	onRowSelect(event: any) {
		try {
			this.router.navigate(['reservations', event.data.reservationId]);
		} catch (error) {
			console.log('onRowSelect(' + event + ')' + error);
		}
	}

	onOpenFormAdd(reservation: ArrivalRow) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.data = { title: 'SMS', reservation };

		const dialogRef = this.dialog.open(SmsComponent, dialogConfig);
		dialogRef.componentInstance.doAdd.subscribe(data => {
			console.log('Dialog output:', data);
			this.markSent(reservation.reservationId);
			dialogRef.close();
		});
	}

	sendNow(row: ArrivalRow) {
		this.sendArrivalSMS(row, false);
	}

	private sendArrivalSMS(row: ArrivalRow, isAuto: boolean) {
		const payload = {
			toMobileCtry: '+1',
			toMobile: row.mobilePhone,
			smsMessage: this.buildArrivalMessage(row),
			sendBySms: true
		};
		this.reservationService.sendSMS(payload).pipe(first()).subscribe(
			() => {
				this.markSent(row.reservationId);
				this.messageService.add({
					severity: 'success',
					summary: isAuto ? 'Auto-sent' : 'Sent',
					detail: `Arrival SMS sent to ${row.guestFirstName} ${row.guestLastName} (${row.propertyCode})`
				});
			},
			err => {
				this.alertService.error(err);
			}
		);
	}

	private buildArrivalMessage(row: ArrivalRow): string {
		let msg = `Welcome to ${row.propertyCode}! Your unit is ready.`;
		if (row.accessCodeFrontDoor) { msg += ` Door code: ${row.accessCodeFrontDoor}.`; }
		if (row.accessCodeHoaGate) { msg += ` Gate code: ${row.accessCodeHoaGate}.`; }
		msg += ' Enjoy your stay! — KBM Resorts';
		return msg;
	}

	private markSent(reservationId: number) {
		const ts = new Date().toISOString();
		this.sendLog.set(reservationId, ts);
		const row = this.reservations.find(r => r.reservationId === reservationId);
		if (row) {
			row.messageSentAt = ts;
			row.boardStatus = 'GREY';
		}
	}

	toggleManualHold(row: ArrivalRow) {
		row.manualHold = !row.manualHold;
		if (row.manualHold) {
			row.boardStatus = 'RED';
		} else {
			// Re-compute
			if (row.messageSentAt) { row.boardStatus = 'GREY'; }
			else if (!row.accessCodeFrontDoor) { row.boardStatus = 'RED'; }
			else if (row.breezewayPct === 100) { row.boardStatus = 'GREEN'; }
			else { row.boardStatus = 'AMBER'; }
		}
	}

	// ─── AI panel ─────────────────────────────────────────────────────────────

	openAiPanel() {
		this.aiDialogService.open({ header: 'Guest Services AI', preferredDomain: 'reservations' });
	}

	// ─── Helpers ──────────────────────────────────────────────────────────────

	getStatusLabel(status: ArrivalRowStatus): string {
		switch (status) {
			case 'GREEN': return 'Ready';
			case 'AMBER': return 'In Progress';
			case 'RED': return 'Flagged';
			case 'GREY': return 'Sent';
			default: return '';
		}
	}

	getStatusSeverity(status: ArrivalRowStatus): string {
		switch (status) {
			case 'GREEN': return 'success';
			case 'AMBER': return 'warning';
			case 'RED': return 'danger';
			case 'GREY': return 'secondary';
			default: return '';
		}
	}

	getResStatusName(resStatus: string) {
		return this.constantService.reservationStatuses.find(item => item.value === resStatus)?.label;
	}

	getResStatusColor(resStatus: string) {
		return this.constantService.reservationStatuses.find(item => item.value === resStatus)?.color;
	}

	clear(table: Table) {
		table.clear();
	}

	reloadPage() {
		window.location.reload();
	}

	getLocationName(locationId: any) {
		if (locationId != null) {
			const loc = this.locations.find((e: any) => e.locationId == locationId);
			return loc ? loc.name : '';
		}
		return '';
	}
}
